import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { ShieldAlert, Loader2, CheckCircle2, XCircle } from "lucide-react";
import Navbar from "@/components/Navbar";

export default function DisavowContactChange() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState("loading"); // loading | ready | resolved | done | error
  const [info, setInfo] = useState(null);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setState("error"); setMsg("This link is missing its token."); return; }
    api.get(`/contact-change/disavow/preview?token=${encodeURIComponent(token)}`)
      .then(({ data }) => {
        setInfo(data);
        setState(data.actionable ? "ready" : "resolved");
      })
      .catch((err) => { setState("error"); setMsg(formatError(err.response?.data?.detail)); });
  }, [token]);

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/contact-change/disavow", { token });
      setState("done"); setMsg(data.message || "The request was cancelled and your account was secured.");
    } catch (err) {
      setState("error"); setMsg(formatError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen text-foreground" data-testid="disavow-contact-change-page">
      <Navbar />
      <div className="flex items-center justify-center px-4 min-h-[calc(100vh-4rem)]">
      <div className="w-full max-w-md border border-blaze/40 bg-surface p-7">
        <div className="flex items-center gap-2 mb-1">
          <ShieldAlert className="w-6 h-6 text-blaze" />
          <h1 className="font-heading text-2xl uppercase tracking-tight">Was this you?</h1>
        </div>

        {state === "loading" && (
          <p className="font-mono text-xs uppercase text-muted-foreground mt-4 inline-flex items-center gap-2" data-testid="disavow-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Checking your link…
          </p>
        )}

        {state === "ready" && (
          <div className="mt-3" data-testid="disavow-ready">
            <p className="text-sm font-body text-secondary-foreground">
              A request was made to change your OpticSwap {info?.field === "email" ? "email address" : info?.field} to{" "}
              <span className="font-mono text-foreground">{info?.masked_value}</span>.
            </p>
            <p className="text-sm font-body text-secondary-foreground mt-3">
              If that was you, do nothing. If it <strong className="text-foreground">wasn't</strong>, confirm below to cancel the
              request, sign out every device on your account, and flag it for our security team.
            </p>
            <button onClick={confirm} disabled={busy} data-testid="disavow-confirm-btn"
              className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-blaze hover:bg-blaze/90 disabled:opacity-50 text-white font-heading font-bold uppercase text-sm px-5 py-3 transition-colors">
              <ShieldAlert className="w-4 h-4" /> {busy ? "Securing…" : "This wasn't me — secure my account"}
            </button>
            <p className="font-mono text-[10px] uppercase text-muted-foreground mt-3">
              After this, sign in again and change your password.
            </p>
          </div>
        )}

        {state === "resolved" && (
          <p className="text-sm font-body text-secondary-foreground mt-4 inline-flex items-start gap-2" data-testid="disavow-resolved">
            <CheckCircle2 className="w-5 h-5 text-olive-bright shrink-0" /> This request has already been resolved — no further action is needed.
          </p>
        )}

        {state === "done" && (
          <div className="mt-4" data-testid="disavow-done">
            <p className="text-sm font-body text-foreground inline-flex items-start gap-2">
              <CheckCircle2 className="w-5 h-5 text-olive-bright shrink-0" /> {msg}
            </p>
            <Link to="/login" className="mt-5 inline-block font-heading font-bold uppercase text-sm bg-olive hover:bg-olive-bright text-white px-5 py-3 transition-colors" data-testid="disavow-login-link">
              Sign in &amp; change password
            </Link>
          </div>
        )}

        {state === "error" && (
          <p className="text-sm font-body text-blaze mt-4 inline-flex items-start gap-2" data-testid="disavow-error">
            <XCircle className="w-5 h-5 shrink-0" /> {msg || "This link is invalid or has already been used."}
          </p>
        )}
      </div>
      </div>
    </div>
  );
}
